import React, { useEffect, useRef, useState } from 'react';

import type { OficinaExterna, RegiaoExterna } from '../../../api/oficinaExternaApi';
// Reuso por IMPORT das classes do modal de filtros da Oficina (`filter-car-modal-*`,
// `filter-column`, `filter-item-label`, `custom-checkbox`, `filter-modal-actions`) — nunca
// recopiar o CSS, e nunca inventar classe nova de filtro (`13-catalogo-de-ui.md`).
import '../../../components/modal/filter-car/FilterCarModal.css';
import '../../../shared/ui/modal-close-button.css';

export type SituacaoFiltro = 'NA_OFICINA' | 'SAIU' | 'TODOS';

export interface FiltrosOficinaExterna {
  situacao: SituacaoFiltro;
  regiaoId: string;
  oficinaId: string;
}

interface Props {
  regioes: RegiaoExterna[];
  oficinas: OficinaExterna[];
  /**
   * O que a lista está filtrando AGORA (at-65, `BUG-V43`). Sem isto o modal reabriria em branco
   * — e como não há resumo dos filtros na tela, quem reabre para conferir leria "nenhum filtro"
   * numa lista filtrada, e um "Aplicar" em seguida limparia tudo em silêncio.
   */
  filtrosAplicados: FiltrosOficinaExterna;
  onAplicar: (filtros: FiltrosOficinaExterna) => void;
  onCancelar: () => void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
}

const SITUACOES: Array<{ valor: SituacaoFiltro; rotulo: string }> = [
  { valor: 'NA_OFICINA', rotulo: 'Na oficina' },
  { valor: 'SAIU', rotulo: 'Já saíram' },
  { valor: 'TODOS', rotulo: 'Todos' },
];

const PADRAO: FiltrosOficinaExterna = { situacao: 'NA_OFICINA', regiaoId: '', oficinaId: '' };

/**
 * Uma opção do filtro, no markup EXATO do `FilterCarModal` (o modal de filtros da Oficina).
 *
 * 🔴 `input type="checkbox"`, nunca `radio`: a regra que esconde o controle nativo é
 * `.filter-item-label input[type="checkbox"] { display: none }`. Com `radio`, o nativo continua
 * visível e aparece AO LADO do quadrado estilizado — dois controles para a mesma escolha. Foi
 * assim que este modal nasceu, e é o que o Victor recusou em 25/09/2026.
 *
 * A escolha continua ÚNICA por coluna (o backend aceita um valor de cada): clicar numa opção
 * substitui a anterior, e a primeira de cada lista ("Todas") é o que limpa.
 *
 * ⚠️ Declarado no MÓDULO, nunca dentro do componente pai: componente criado no corpo de outro é
 * um tipo novo a cada render, e o React desmonta e remonta a árvore inteira dele.
 */
const Opcao: React.FC<{ rotulo: string; marcado: boolean; onSelecionar: () => void }> = ({
  rotulo,
  marcado,
  onSelecionar,
}) => (
  <label className={`filter-item-label ${marcado ? 'selected' : ''}`}>
    <input type="checkbox" checked={marcado} onChange={onSelecionar} />
    <div className="custom-checkbox">{marcado && <span className="filter-item-checkmark">&#x2714;</span>}</div>
    <span className="filter-text">{rotulo}</span>
  </label>
);

/**
 * Filtros da tela de acompanhamento (at-114).
 *
 * ⚠️ **Montagem condicional** (`{aberto && <FiltrosOficinaExternaModal ... />}`), nunca prop
 * `isOpen`: é o que faz o `useState` inicial ser a única fonte do rascunho. Com o modal sempre
 * montado, o rascunho descartado no "Cancelar" sobrevive para a abertura seguinte (at-61/at-65).
 *
 * **Aplicação em bloco**: o estado é local e só sai no "Aplicar Filtros" — N ajustes geram UMA
 * requisição, e "Cancelar" desfaz de verdade.
 */
const FiltrosOficinaExternaModal: React.FC<Props> = ({
  regioes,
  oficinas,
  filtrosAplicados,
  onAplicar,
  onCancelar,
  sidebarRef,
}) => {
  const [situacao, setSituacao] = useState<SituacaoFiltro>(filtrosAplicados.situacao);
  const [regiaoId, setRegiaoId] = useState(filtrosAplicados.regiaoId);
  const [oficinaId, setOficinaId] = useState(filtrosAplicados.oficinaId);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const cliqueFora = (evento: MouseEvent) => {
      const alvo = evento.target as Node;
      const foraDoModal = modalRef.current && !modalRef.current.contains(alvo);
      const foraDaSidebar = !sidebarRef?.current || !sidebarRef.current.contains(alvo);
      if (foraDoModal && foraDaSidebar) onCancelar();
    };

    document.addEventListener('mousedown', cliqueFora);
    return () => document.removeEventListener('mousedown', cliqueFora);
  }, [onCancelar, sidebarRef]);

  // Trocar de região invalida a oficina escolhida — senão o filtro sai com uma combinação que
  // não existe e a lista volta vazia sem explicação.
  const oficinasDaRegiao = regiaoId
    ? oficinas.filter((o) => (o.regiao?.id ?? o.regiaoId) === regiaoId)
    : oficinas;


  return (
    <div className="filter-car-modal-overlay">
      <div className="filter-car-modal-content" ref={modalRef}>
        <div className="filter-car-modal-header">
          <div className="header-info">
            <h2>Filtrar Veículos</h2>
          </div>
          <button className="close-button" onClick={onCancelar} aria-label="Fechar modal">
            &times;
          </button>
        </div>

        <div className="filter-car-modal-body">
          <div className="filter-columns-container">
            <div className="filter-column">
              <div className="filter-column-title">Situação</div>
              <div className="filter-checkbox-group">
                {SITUACOES.map(({ valor, rotulo }) => (
                  <Opcao
                    key={valor}
                    rotulo={rotulo}
                    marcado={situacao === valor}
                    onSelecionar={() => setSituacao(valor)}
                  />
                ))}
              </div>
            </div>

            <div className="filter-column">
              <div className="filter-column-title">Região</div>
              <div className="filter-checkbox-group">
                <Opcao
                  rotulo="Todas"
                  marcado={regiaoId === ''}
                  onSelecionar={() => {
                    setRegiaoId('');
                    setOficinaId('');
                  }}
                />
                {regioes.map((regiao) => (
                  <Opcao
                    key={regiao.id}
                    rotulo={regiao.nome}
                    marcado={regiaoId === regiao.id}
                    onSelecionar={() => {
                      setRegiaoId(regiao.id);
                      setOficinaId('');
                    }}
                  />
                ))}
              </div>
            </div>

            <div className="filter-column">
              <div className="filter-column-title">Oficina</div>
              <div className="filter-checkbox-group">
                <Opcao
                  rotulo="Todas"
                  marcado={oficinaId === ''}
                  onSelecionar={() => setOficinaId('')}
                />
                {oficinasDaRegiao.map((oficina) => (
                  <Opcao
                    key={oficina.id}
                    rotulo={oficina.nome}
                    marcado={oficinaId === oficina.id}
                    onSelecionar={() => setOficinaId(oficina.id)}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="filter-modal-actions">
            <button
              className="clear-button"
              type="button"
              onClick={() => {
                setSituacao(PADRAO.situacao);
                setRegiaoId(PADRAO.regiaoId);
                setOficinaId(PADRAO.oficinaId);
              }}
            >
              Limpar Filtros
            </button>
            <button
              className="apply-button"
              type="button"
              onClick={() => {
                onAplicar({ situacao, regiaoId, oficinaId });
                onCancelar();
              }}
            >
              Aplicar Filtros
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FiltrosOficinaExternaModal;
